import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Subject, takeUntil } from 'rxjs';

import { CondensedOrderItemUI, Discount, DonationProductItem } from '@azavista/payments';
import { ShoppingCartResultUI } from '@azavista/payments/lib/shopping-cart/processor-ui';
import { ShoppingCartV2Service } from './shopping-cart-v2.service';
import { ICartItem, IShoppingCartV2ChangedData, IShoppingCartV2Data } from './interfaces';
import { UtilsService } from '../shared/utils.service';
import { ApiService } from '../shared/api.service';
import { IPaymentProduct, IProductVisibility, PayById } from '../shared/interfaces';
import { ICheckDiscountCodeResponse, ProductType } from '../shared/interfaces';
import { IApplyDependenciesResult, IIdWithValue } from '../interfaces';
import { TRANSLATION_MAP, TranslationKey } from '../enums';

@Component({
    selector: 'aza-rws-shopping-cart-v2',
    templateUrl: 'shopping-cart-v2.component.html',
})
export class ShoppingCartV2Component implements OnInit, OnDestroy {
    cartItems: ICartItem[] = [];
    cartResultUI = this.cartV2Svc.createEmptyUIResult();
    condensedItems: CondensedOrderItemUI[] = [];
    discountCodeInvalid = false;
    discountCode = '';
    cartData: IShoppingCartV2Data | null;
    payByOptions: IIdWithValue[] = [];
    payByVisibilityMap?: Record<PayById, boolean>;
    /**
     * During updating `selectedPayByOption`,
     * please also make sure to call `onPayByInvoiceChanged()` to emit the payment changes
     **/
    selectedPayByOption: IIdWithValue;
    // showTransactions = false;
    cartProductsVisibility: IProductVisibility[];
    flags: IFlags = {};
    cartCurrency = '';
    translations = TRANSLATION_MAP;

    @Input()
    set data(data: IShoppingCartV2Data | null) {
        this.setData(data);
        this.applyDependencyResult();
    }

    @Input()
    set productsVisibility(visibility: IProductVisibility[]) {
        this.cartProductsVisibility = visibility;
        // Send current cartItems so the calculated have the same selectedQuantity values
        this.cartItems = this.getCartItemsFromComputedCartItems(this.cartItems);
        this.refreshCartResultUI();
        this.shoppingCartChanged.next(this.getChangedData());
    }

    private _dependencyResult?: IApplyDependenciesResult;
    @Input()
    set dependencyResult(result: IApplyDependenciesResult) {
        this._dependencyResult = result || this._dependencyResult;
        this.applyDependencyResult();
    };

    @Output() shoppingCartChanged = new EventEmitter<IShoppingCartV2ChangedData>();

    private discountCodeSearchTextChangedSubject = new Subject<string>();
    private destroyed$ = new Subject<void>();
    private discountCodeDebounceTime = 500;
    private discount: Discount | null = null;
    private checkDiscountCodeRes: ICheckDiscountCodeResponse | null = null;

    constructor(
        private readonly cartV2Svc: ShoppingCartV2Service,
        private readonly utilsSvc: UtilsService,
        private readonly apiSvc: ApiService,
        private readonly translateSvc: TranslateService
    ) {
    }

    ngOnInit(): void {
        this.flags.cartItemsSelected = true;
        this.flags.transactionItemsSelected = false;
        this.payByOptions = this.createPayByOptions();
        this.translateSvc.get(TranslationKey.payByInvoice)
            .pipe(takeUntil(this.destroyed$))
            .subscribe(value => this.payByOptions = this.createPayByOptions());
        // this.utilsSvc.getDebouncedDistinctObservable(this.discountCodeSearchTextChangedSubject, this.discountCodeDebounceTime)
        //     .pipe(takeUntil(this.destroyed$))
        //     .subscribe(data => this.processDiscountCodeSearchTextChanged(data));
        this.discountCodeSearchTextChangedSubject
            .pipe(takeUntil(this.destroyed$))
            .subscribe(data => this.processDiscountCodeSearchTextChanged(data));
    }

    async processDiscountCodeSearchTextChanged(code: string): Promise<void> {
        this.discount = null;
        this.discountCodeInvalid = false;
        this.discount = null;
        code = code?.trim();
        if (!code) {
            this.refreshCartResultUI();
            return;
        }
        if (this.cartData.readOnly) {
            return;
        }
        this.checkDiscountCodeRes = await this.apiSvc.checkDiscountCode(code);
        this.discountCodeInvalid = !this.checkDiscountCodeRes.discount_code_valid;
        if (this.checkDiscountCodeRes.discount && this.checkDiscountCodeRes.discount_code_valid) {
            this.discount = {
                discount_code: code,
                discount_type: this.checkDiscountCodeRes.discount.discount_type,
                global: !!this.checkDiscountCodeRes.discount.global,
                id: this.checkDiscountCodeRes.discount.id,
                status: this.checkDiscountCodeRes.discount.status,
                value: this.checkDiscountCodeRes.discount.value,
            };
        }
        this.refreshCartResultUI();
    }

    // TODO: This is deprecated since the new cart has increase/decrease quantity buttons
    onCartItemQuantityChanged(item: ICartItem, ev: Event): void {
        const value = +(ev.target as HTMLInputElement).value;
        item.selectedQuantity = value || 0;
        this.refreshCartResultUI();
        this.shoppingCartChanged.next(this.getChangedData());
    }

    onCartItemQuantityIncreased(item: ICartItem): void {
        if (item.soldOut) {
            return;
        }
        if (item.quantityConstrained && item.selectedQuantity >= item.maxQuantity) {
            // Max quantity is constrained and reached
            return;
        }
        item.selectedQuantity++;
        this.refreshCartResultUI();
        this.shoppingCartChanged.next(this.getChangedData());
    }

    onCartItemQuantityDecreased(item: ICartItem): void {
        if (item.selectedQuantity <= (item.minQuantity ?? 0)) {
            return;
        }
        item.selectedQuantity--;
        this.refreshCartResultUI();
        this.shoppingCartChanged.next(this.getChangedData());
    }

    onApplyDiscoundCode(): void {
        if (this.cartData.readOnly) {
            return;
        }
        this.discountCodeSearchTextChangedSubject.next(this.discountCode);
        this.shoppingCartChanged.next(this.getChangedData());
    }

    onDiscountCodeInput(code: string): void {
        if (this.cartData.readOnly) {
            return;
        }
        this.discountCodeSearchTextChangedSubject.next(code);
        this.shoppingCartChanged.next(this.getChangedData());
    }

    getChangedData(): IShoppingCartV2ChangedData {
        const visibleCartItems = this.cartItems.filter(x => !x.hidden);
        const visibleTicketCartItems = visibleCartItems.filter(x => x.product.type === ProductType.ticket);
        let validateItemsResult: boolean;
        if (visibleTicketCartItems.length > 0) {
            validateItemsResult = this.cartV2Svc.validateItems(this.cartV2Svc.getProcessorCartItems(visibleCartItems));
        } else {
            // TODO: For now the payment library returns false if we provide empty array to validateItems function
            //       This happens if all ticket type products are hidden (by dependency)
            //       We will handle this case in the participant app until the library is changed
            // There are no visible ticket products - assume items are valid
            validateItemsResult = true;
        }
        const result: IShoppingCartV2ChangedData = {
            visibleProductsWithQuantity: this.cartV2Svc.createProductsWithQuantityFromCartItems(visibleCartItems),
            donationProducts: this.cartResultUI.donationProducts,
            discount: this.checkDiscountCodeRes?.discount,
            discountCode: this.checkDiscountCodeRes?.discount?.discount_code,
            total: this.cartResultUI.totals.total,
            currentOrderTotal: this.cartResultUI.totals.current_order_total,
            selectedPayByOption: this.selectedPayByOption?.id as PayById || null,
            areItemsValid: validateItemsResult,
            visibleTicketProducts: this.cartV2Svc.createProductsWithQuantityFromCartItems(visibleTicketCartItems),
            cartCurrency: this.getCartCurrency(),
        };
        return result;
    }

    onCartTabSelected(): void {
        this.flags.cartItemsSelected = true;
        this.flags.transactionItemsSelected = false;
        this.flags.showTransactions = false;
    }

    onTransactionsTabSelected(): void {
        this.flags.transactionItemsSelected = true;
        this.flags.cartItemsSelected = false;
        this.flags.showTransactions = true;
    }

    onShowTransactions(): void {
        this.flags.showTransactions = true;
    }

    onHideTransactions(): void {
        this.flags.showTransactions = false;
    }

    ngOnDestroy(): void {
        this.destroyed$.next();
        this.destroyed$.complete();
    }

    private getCartCurrency(): string {
        return this.cartData?.productRespone?.products?.[0]?.currency || '';
    }

    private createPayByOptions(): IIdWithValue[] {
        const payByOptions = [
            { id: PayById.invoice, value: this.translateSvc.instant(TranslationKey.payByInvoice) },
            { id: PayById.online, value: this.translateSvc.instant(TranslationKey.payOnline) },
        ];
        return payByOptions;
    }

    private setData(data: IShoppingCartV2Data | null): void {
        this.cartData = data;
        this.cartV2Svc.init(data);
        if (!data) {
            return;
        }
        this.cartCurrency = this.getCartCurrency();
        // Send null as current cartItems so the calculated items will have quantities set in the data (order)
        this.cartItems = this.getCartItemsFromComputedCartItems(null);
        this.refreshCartResultUI();
        this.shoppingCartChanged.next(this.getChangedData());
    }

    private refreshCartResultUI(): void {
        this.cartResultUI = this.getShoppingCartResult(
            this.cartItems.filter(x => !x.hidden),
            this.cartData?.productRespone?.pay_by_invoice,
            this.discount
        );
    }

    /**
     *
     * @param data
     * @param currentCartItems If provided, the result items will have selectedQuantity of the corresponding currentCartItems item.
     * Should be provided when only the product dependencies are changed. Set it to null on the initial creation of the items
     * @returns
     */
    private getCartItemsFromComputedCartItems(currentCartItems: ICartItem[] | null): ICartItem[] {
        if (!this.cartV2Svc.processor) {
            return [];
        }

        const computedCartItems = this.cartV2Svc.computeCartItems();
        const cartItems = this.cartV2Svc.getCartItems(computedCartItems, this.cartProductsVisibility);
        const processedCartItems: ICartItem[] = [];
        for (const item of computedCartItems) {
            const cartItem = cartItems.find(x => x.product.id === item.product.id);
            if (cartItem) {
                // If there is existing item, we use its selected quantity, otherwise we use quantity from the order
                // This helps reverting to the quantity that the user has set before the product was removed because of dependency and then added again
                // because the dependency evaluated value has changed back
                const currentCartItem = currentCartItems?.find(x => x.product.id === item.product.id);
                cartItem.selectedQuantity = currentCartItem ? currentCartItem.selectedQuantity : item.cart_quantity;
                cartItem.selectedQuantity = Math.max(cartItem.selectedQuantity, cartItem.minQuantity ?? 0);
                if (cartItem.quantityConstrained) {
                    cartItem.selectedQuantity = Math.min(cartItem.selectedQuantity, cartItem.maxQuantity);
                }
                processedCartItems.push(cartItem);
            }
        }

        return processedCartItems;
    }

    getShoppingCartResult(cartItems: ICartItem[], payByInvoice: boolean, discount: Discount | null): ShoppingCartResultUI {
        const processorCartItems = this.cartV2Svc.getProcessorCartItems(cartItems);
        const cartResult = this.cartV2Svc.process({
            cartItems: processorCartItems,
            discount: discount,
            pay_by_invoice: payByInvoice
        });
        return cartResult;
    }

    onPayByInvoiceChanged(): void {
        this.shoppingCartChanged.next(this.getChangedData());
    }

    applyDependencyResult() {
        if (!this._dependencyResult) {
            return;
        }
        const { dependencyResults } = this._dependencyResult;
        const { invoicePaymentDependency, onlinePaymentDependency, productRespone } = this.cartData ?? {};
        const isInvoicePaymentEnabled = !invoicePaymentDependency ||
            dependencyResults.some(({ result, dependency }) => dependency.id === invoicePaymentDependency && result);
        const isOnlinePaymentEnabled = !onlinePaymentDependency
            || !isInvoicePaymentEnabled
            || dependencyResults.some(({ result, dependency }) => dependency.id === onlinePaymentDependency && result);
        const oldPayByVisibilityMap = { ...this.payByVisibilityMap };
        this.payByVisibilityMap = {
            [PayById.invoice]: isInvoicePaymentEnabled,
            [PayById.online]: isOnlinePaymentEnabled,
        };
        // if the dependency affect the available payment methods, then update them to `undefined`
        if (productRespone?.pay_by_invoice && this.selectedPayByOption?.id && (isInvoicePaymentEnabled !== oldPayByVisibilityMap.invoice || isOnlinePaymentEnabled !== oldPayByVisibilityMap.online)) {
            this.selectedPayByOption = undefined;
            this.onPayByInvoiceChanged();
        }
    }

    onDonationPriceChanges(product_id: string, ev: Event) {
        // The participant should not be able to provide donation value less than the donation product price
        const cappedDonationValue = this.capDonationValue(product_id, ev.target as HTMLInputElement);
        const donationPrices: DonationProductItem[] = this.cartResultUI.donationProducts.map(item => ({
            ...item,
            price: item.product_id === product_id ? cappedDonationValue : item.price
        }));
        this.cartV2Svc.processor.setDonationPrices(donationPrices);
        this.refreshCartResultUI();
    }

    capDonationValue(productId: string, inputEl: HTMLInputElement): number {
        const selectedPrice = inputEl.valueAsNumber;
        const initialProductPrice = this.cartData.productRespone.products.find(x => x.id === productId).price;
        let cappedValue: number;
        if (selectedPrice >= initialProductPrice) {
            cappedValue = selectedPrice;
        } else {
            cappedValue = initialProductPrice;
            inputEl.value = `${cappedValue}`;
        }
        return cappedValue;
    }

    getDonationProductParticipantPrice(cartItem: ICartItem): number {
        const donationProduct = this.cartResultUI.donationProducts.find(x => x.product_id === cartItem.product.id);
        return donationProduct ? donationProduct.price : cartItem.product.price;
    }
}

interface IFlags {
    cartItemsSelected?: boolean;
    transactionItemsSelected?: boolean;
    showTransactions?: boolean;
}
